// ============================================================
// THE COMPANY — RichTextEditor Component (Tiptap)
// ============================================================

import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  editable?: boolean;
}

export default function RichTextEditor({
  content,
  onChange,
  placeholder = "Start writing...",
  editable = true,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content,
    editable,
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
  });

  // Sync content prop changes from outside
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== content) {
      editor.commands.setContent(content, false);
    }
  }, [editor, content]);

  if (!editor) return null;

  return (
    <div className="rounded-lg border border-[var(--border-default)] overflow-hidden">
      {editable && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}

// --- Toolbar ---

interface ToolbarProps {
  editor: ReturnType<typeof useEditor>;
}

interface ToolbarButton {
  label: string;
  action: () => void;
  isActive: boolean;
}

function Toolbar({ editor }: ToolbarProps) {
  if (!editor) return null;

  const groups: (ToolbarButton | "divider")[][] = [
    [
      {
        label: "B",
        action: () => editor.chain().focus().toggleBold().run(),
        isActive: editor.isActive("bold"),
      },
      {
        label: "I",
        action: () => editor.chain().focus().toggleItalic().run(),
        isActive: editor.isActive("italic"),
      },
    ],
    ["divider"],
    [
      {
        label: "H1",
        action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
        isActive: editor.isActive("heading", { level: 1 }),
      },
      {
        label: "H2",
        action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
        isActive: editor.isActive("heading", { level: 2 }),
      },
      {
        label: "H3",
        action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
        isActive: editor.isActive("heading", { level: 3 }),
      },
    ],
    ["divider"],
    [
      {
        label: "\u2022",
        action: () => editor.chain().focus().toggleBulletList().run(),
        isActive: editor.isActive("bulletList"),
      },
      {
        label: "1.",
        action: () => editor.chain().focus().toggleOrderedList().run(),
        isActive: editor.isActive("orderedList"),
      },
    ],
    ["divider"],
    [
      {
        label: "</>",
        action: () => editor.chain().focus().toggleCode().run(),
        isActive: editor.isActive("code"),
      },
      {
        label: "\u201C",
        action: () => editor.chain().focus().toggleBlockquote().run(),
        isActive: editor.isActive("blockquote"),
      },
    ],
  ];

  return (
    <div className="tiptap-toolbar">
      {groups.map((group, gi) =>
        group.map((item, ii) => {
          if (item === "divider") {
            return <div key={`d-${gi}`} className="toolbar-divider" />;
          }
          return (
            <button
              key={`${gi}-${ii}`}
              type="button"
              onClick={item.action}
              className={item.isActive ? "is-active" : ""}
              title={item.label}
            >
              {item.label}
            </button>
          );
        }),
      )}
    </div>
  );
}
